import { io, type Socket } from 'socket.io-client';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import type { ScraperRunStatus, SitemapAlert, SocialAlert } from './types';

/* ────────────────────────────────────────────────────────────────────────────
 * `scraper_status_update` carries three unrelated payloads from three
 * different producers (worker/event_relay.py maps one Redis channel 1:1 onto
 * one socket event). The scheduler's payload has no `type` field, which is
 * exactly what makes `type?: undefined` a usable discriminant.
 * ──────────────────────────────────────────────────────────────────────────── */

/** worker/scheduler.py */
export interface ScraperRunEvent {
  type?: undefined;
  name: string;
  status: ScraperRunStatus;
  last_run?: string;
  execution_time?: number;
  next_run?: number | null;
  last_output?: string;
  error?: string | null;
  error_type?: string | null;
}

/** raffle_sites/sitemap_monitor.py + social/{instagram,facebook}_monitor.py */
export type ScraperStatusUpdate = ScraperRunEvent | SitemapAlert | SocialAlert;

export interface ScraperOutputEvent {
  name: string;
  output: string;
  ts: number;
}

export interface DeployStatusEvent {
  phase: 'pulling' | 'building' | 'restarting' | 'done' | 'error';
  log?: string;
  ts: number;
  summary?: string;
}

interface ServerToClientEvents {
  scraper_status_update: (d: ScraperStatusUpdate) => void;
  scraper_output: (d: ScraperOutputEvent) => void;
  deploy_status: (d: DeployStatusEvent) => void;
}
/** The client never emits custom events — all control flows through REST. */
type ClientToServerEvents = Record<string, never>;

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

// ── runtime guards (the union arrives as JSON, unverified by TS) ─────────────
export const isSitemapAlert = (e: ScraperStatusUpdate): e is SitemapAlert =>
  e.type === 'new_url_discovered';

export const isSocialAlert = (e: ScraperStatusUpdate): e is SocialAlert =>
  e.type === 'social_raffle';

export const isScraperRun = (e: ScraperStatusUpdate): e is ScraperRunEvent =>
  e.type === undefined && typeof (e as ScraperRunEvent).name === 'string';

// ── tiny event bus ──────────────────────────────────────────────────────────
class Bus<T> {
  private listeners = new Set<(e: T) => void>();
  subscribe = (fn: (e: T) => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };
  emit = (e: T) => {
    this.listeners.forEach((fn) => fn(e));
  };
}

export const runBus = new Bus<ScraperRunEvent>();
export const sitemapBus = new Bus<SitemapAlert>();
export const socialBus = new Bus<SocialAlert>();
export const outputBus = new Bus<ScraperOutputEvent>();
export const deployBus = new Bus<DeployStatusEvent>();

// ── connection state, exposed via useSyncExternalStore ──────────────────────
let connected = false;
const connectionListeners = new Set<() => void>();
function setConnected(next: boolean) {
  if (connected === next) return;
  connected = next;
  connectionListeners.forEach((fn) => fn());
}

// ── the single connection ───────────────────────────────────────────────────
let socket: TypedSocket | null = null;

/** Lazily created module singleton, so React StrictMode's double-mount cannot
 *  open two connections. Never torn down — it lives as long as the page. */
export function getSocket(): TypedSocket {
  if (socket) return socket;

  socket = io({
    path: '/socket.io',
    // websocket first: nginx already has the upgrade block with
    // proxy_read_timeout 86400. Polling stays as a fallback.
    transports: ['websocket', 'polling'],
  });

  socket.on('connect', () => setConnected(true));
  socket.on('disconnect', () => setConnected(false));

  // Demux once, here. No view should ever see the raw union.
  socket.on('scraper_status_update', (e) => {
    if (isScraperRun(e)) runBus.emit(e);
    else if (isSitemapAlert(e)) sitemapBus.emit(e);
    else if (isSocialAlert(e)) socialBus.emit(e);
    else console.warn('unhandled scraper_status_update payload', e);
  });

  socket.on('scraper_output', (e) => outputBus.emit(e));
  socket.on('deploy_status', (e) => deployBus.emit(e));

  return socket;
}

// ── hooks ───────────────────────────────────────────────────────────────────
/** Subscribes without re-binding on every render, so callers may pass inline
 *  arrow functions without churning the subscription. */
function useBus<T>(bus: Bus<T>, cb: (e: T) => void) {
  const ref = useRef(cb);
  useEffect(() => {
    ref.current = cb;
  });
  useEffect(() => {
    getSocket();
    return bus.subscribe((e) => ref.current(e));
  }, [bus]);
}

export const useScraperRunEvents = (cb: (e: ScraperRunEvent) => void) => useBus(runBus, cb);
export const useSitemapAlerts = (cb: (e: SitemapAlert) => void) => useBus(sitemapBus, cb);
export const useSocialAlerts = (cb: (e: SocialAlert) => void) => useBus(socialBus, cb);
export const useDeployStatus = (cb: (e: DeployStatusEvent) => void) => useBus(deployBus, cb);

/** Output for one scraper. Filtering here keeps the name check out of views. */
export function useScraperOutput(name: string | null, cb: (output: string, ts: number) => void) {
  const ref = useRef(cb);
  useEffect(() => {
    ref.current = cb;
  });
  useEffect(() => {
    if (!name) return;
    getSocket();
    return outputBus.subscribe((e) => {
      if (e.name === name) ref.current(e.output, e.ts);
    });
  }, [name]);
}

export function useSocketConnected(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      getSocket();
      connectionListeners.add(onChange);
      return () => connectionListeners.delete(onChange);
    },
    () => connected,
    () => false,
  );
}
